import { AddLinkRounded } from '@mui/icons-material';

import Coupon from '@checkout/components/Coupon';
import Installments from '@checkout/components/Installments';
import MethodSelect from '@checkout/components/MethodSelect';
import PixInfo from '@checkout/components/PixInfo';
import PlanCard from '@checkout/components/PlanCard';
import SubmitButton from '@checkout/components/SubmitButton';
import Input from '@components/Input';
import Text from '@components/Text';
import { t } from '@i18n';

import type { CheckoutValuesRef } from '../types';

import './AsaasPaymentScreen.scss';

interface AsaasPaymentScreenProps {
  valuesRef: CheckoutValuesRef;
  plan: any;
  planForDisplay: any;
  provider: string;
  methodsEnabled: string[];
  refreshKey: number;
  refresh: () => void;
  pixInfo: any;
  isLoading: boolean;
  onPay: () => void;
  submitDisabled?: boolean;
}

export function AsaasPaymentScreen({
  valuesRef,
  plan,
  planForDisplay,
  provider,
  methodsEnabled,
  refreshKey,
  refresh,
  pixInfo,
  isLoading,
  onPay,
  submitDisabled = false,
}: AsaasPaymentScreenProps) {
  const card = valuesRef.current.card;

  return (
    <>
      {methodsEnabled.length > 1 && <Text.h5 className='mt-3'>{t('checkout.paymentMethod')}</Text.h5>}

      <MethodSelect
        initial={valuesRef.current.method}
        valuesRef={valuesRef}
        refreshHandler={refresh}
        provider={provider}
      />

      {valuesRef.current.method === 'credit' && <>
        <Text.h5 className='mt-3'>{t('checkout.cardData')}</Text.h5>
        <div className='input-group'>
          <Input.CardNumber
            data-error='card-number'
            bypassStyles
            label={t('input.cardNumber.label')}
            placeholder={t('checkout.card.maskPlaceholder')}
            value={card.number}
            onInput={(value: string) => { card.number = value; }}
          />
          <div className='card-bottom-section'>
            <div className='card-expiration-box'>
              <Input.Select
                dataError='card-exp-month'
                bypassStyles
                placeholder={t('checkout.card.expMonth')}
                value={card.expMonth}
                onInput={(value: string) => { card.expMonth = value; }}
                options={[
                  { label: `01 - ${t('month.01')}`, value: '01' },
                  { label: `02 - ${t('month.02')}`, value: '02' },
                  { label: `03 - ${t('month.03')}`, value: '03' },
                  { label: `04 - ${t('month.04')}`, value: '04' },
                  { label: `05 - ${t('month.05')}`, value: '05' },
                  { label: `06 - ${t('month.06')}`, value: '06' },
                  { label: `07 - ${t('month.07')}`, value: '07' },
                  { label: `08 - ${t('month.08')}`, value: '08' },
                  { label: `09 - ${t('month.09')}`, value: '09' },
                  { label: `10 - ${t('month.10Label')}`, value: '10' },
                  { label: `11 - ${t('month.11Label')}`, value: '11' },
                  { label: `12 - ${t('month.12')}`, value: '12' },
                ]}
              />
              <Input.Select
                dataError='card-exp-year'
                bypassStyles
                placeholder={t('checkout.card.expYear')}
                value={card.expYear}
                onInput={(value: string) => { card.expYear = value; }}
                options={Array.from({ length: 13 }, (_, index) => {
                  const year = new Date().getFullYear() + index;
                  return { value: year.toString(), label: year.toString() };
                })}
              />
            </div>
            <Input.Integer
              className='card-cvv-input'
              data-error='card-cvv'
              bypassStyles
              max={3}
              placeholder={t('checkout.card.cvv')}
              value={card.cvv}
              onInput={(value: string) => { card.cvv = value; }}
              inputProps={{ autoComplete: 'cc-csc' }}
            />
          </div>
        </div>

        <div className='input-group'>
          <Input.FullName
            data-error='card-holder-name'
            bypassStyles
            placeholder={t('checkout.card.holderName')}
            value={card.holderName}
            onInput={(value: string) => { card.holderName = value; }}
            inputProps={{ autoComplete: 'cc-name' }}
          />
        </div>

        <span className='utilize-name-link' onClick={() => {
          card.holderName = valuesRef.current.name;
          refresh();
        }}>
          <AddLinkRounded />
          <Text.small>{t('checkout.useFilledName')}</Text.small>
        </span>
      </>}

      <Installments
        key={`installments#${refreshKey}`}
        plan={plan}
        valuesRef={valuesRef}
        refreshHandler={refresh}
        provider={provider}
      />

      <Text.h5 className='mt-2'>{t('checkout.document')}</Text.h5>
      <div className='input-group'>
        <Input.CPF
          data-error='cpf'
          bypassStyles
          placeholder={t('checkout.document.cpfHolder')}
          value={valuesRef.current.cpf}
          onInput={(value: string) => { valuesRef.current.cpf = value; }}
        />
      </div>

      {!pixInfo && <div className='input-group mt-3'>
        <Coupon valuesRef={valuesRef} onCouponApplied={refresh} />
      </div>}

      {pixInfo && <PixInfo pixInfo={pixInfo} />}

      {/* mobile-only twin of the summary footer's card (see .checkout-plan-card--inline) */}
      <PlanCard
        plan={planForDisplay}
        valuesRef={valuesRef}
        className='checkout-plan-card--inline'
      />

      <SubmitButton
        valuesRef={valuesRef}
        onPress={onPay}
        loading={isLoading}
        plan={planForDisplay}
        disabled={submitDisabled}
      />
    </>
  );
}
